package com.example.event_management_system.ui.screens.calendar

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ExitToApp
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.KeyboardArrowLeft
import androidx.compose.material.icons.filled.KeyboardArrowRight
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material.icons.filled.SwapHoriz
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandHorizontally
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkHorizontally
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.outlined.CalendarToday
import androidx.compose.material.icons.outlined.EventNote
import androidx.compose.material.icons.outlined.ExpandMore
import androidx.compose.material.icons.outlined.FolderOpen
import androidx.compose.material.icons.outlined.HelpOutline
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material.icons.outlined.Settings
import androidx.compose.material.icons.outlined.Shield
import androidx.compose.material.icons.outlined.FilterList
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.ui.components.calendar.CalendarDayView
import com.example.event_management_system.ui.components.calendar.CalendarGrid
import com.example.event_management_system.ui.components.calendar.CalendarWeekView
import com.example.event_management_system.ui.components.calendar.CalendarYearView
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.components.event.EventListItem
import com.example.event_management_system.ui.screens.events.EventDetailScreen
import com.example.event_management_system.ui.screens.events.EventFormDialog
import com.example.event_management_system.ui.screens.events.EventFormScreen
import com.example.event_management_system.ui.screens.admin.AdminDashboardScreen
import com.example.event_management_system.ui.screens.events.EventListScreen
import com.example.event_management_system.ui.screens.login.LoginScreen
import com.example.event_management_system.ui.screens.settings.SettingsScreen
import com.example.event_management_system.ui.theme.EventCancelled
import com.example.event_management_system.ui.theme.EventCancelledAccent
import com.example.event_management_system.ui.theme.EventCompleted
import com.example.event_management_system.ui.theme.EventCompletedAccent
import com.example.event_management_system.ui.theme.EventConfirmed
import com.example.event_management_system.ui.theme.EventConfirmedAccent
import com.example.event_management_system.ui.theme.EventPending
import com.example.event_management_system.ui.theme.EventPendingAccent
import com.example.event_management_system.util.currentLocalDate
import com.example.event_management_system.util.currentLocalDateTime
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

enum class DateRangeFilter(val label: String) {
    ALL("All"), TODAY("Today"), THIS_WEEK("This Week"), THIS_MONTH("This Month")
}

enum class GuestCountFilter(val label: String) {
    ANY("Any"), SMALL("< 25"), MEDIUM("25–50"), LARGE("50–100"), XLARGE("100+")
}

data class CalendarScreen(val initialLocationId: String? = null) : Screen {

    @OptIn(ExperimentalMaterial3Api::class)
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val screenModel = getScreenModel<CalendarScreenModel>()
        val state by screenModel.state.collectAsState()

        LaunchedEffect(initialLocationId) {
            if (initialLocationId != null) screenModel.selectLocation(initialLocationId)
        }

        LaunchedEffect(state.isSignedOut) {
            if (state.isSignedOut) navigator.replaceAll(LoginScreen())
        }

        val today = currentLocalDate()
        val nowLocalDateTime = currentLocalDateTime()
        val upcomingEvents = state.events
            .filter { event ->
                val eventDate = Instant.fromEpochMilliseconds(event.eventDate)
                    .toLocalDateTime(TimeZone.UTC).date
                when {
                    eventDate > today -> true
                    eventDate < today -> false
                    else -> {
                        val parsed = parseEventTime(event.eventTime)
                        if (parsed == null) true
                        else {
                            val (h, m) = parsed
                            h > nowLocalDateTime.hour ||
                                (h == nowLocalDateTime.hour && m >= nowLocalDateTime.minute)
                        }
                    }
                }
            }
            .sortedWith(compareBy({ it.eventDate }, { it.eventTime }))
            .take(7)
        val selectedLocation = state.locations.find { it.id == state.selectedLocationId }

        var sidebarExpanded by remember { mutableStateOf(true) }
        var showFilters by remember { mutableStateOf(false) }
        var showAddEventDialog by remember { mutableStateOf(false) }
        var addEventPrefilledDate by remember { mutableStateOf<Long?>(null) }
        var selectedStatuses by remember { mutableStateOf(setOf<EventStatus>()) }
        var selectedDateRange by remember { mutableStateOf(DateRangeFilter.ALL) }
        var selectedGuestCount by remember { mutableStateOf(GuestCountFilter.ANY) }

        val filtersActive = selectedStatuses.isNotEmpty() ||
            selectedDateRange != DateRangeFilter.ALL ||
            selectedGuestCount != GuestCountFilter.ANY
        val activeFilterCount = (if (selectedStatuses.isNotEmpty()) 1 else 0) +
            (if (selectedDateRange != DateRangeFilter.ALL) 1 else 0) +
            (if (selectedGuestCount != GuestCountFilter.ANY) 1 else 0)

        val filteredEvents = state.events.filter { event ->
            val q = state.searchQuery
            val matchesSearch = q.isBlank() ||
                event.customerName.contains(q, ignoreCase = true) ||
                event.locationName.contains(q, ignoreCase = true)
            val matchesStatus = selectedStatuses.isEmpty() || event.status in selectedStatuses
            val evtDate = Instant.fromEpochMilliseconds(event.eventDate).toLocalDateTime(TimeZone.UTC).date
            val matchesDate = when (selectedDateRange) {
                DateRangeFilter.ALL -> true
                DateRangeFilter.TODAY -> evtDate == today
                DateRangeFilter.THIS_WEEK -> {
                    val offsetFromMonday = when (today.dayOfWeek) {
                        kotlinx.datetime.DayOfWeek.MONDAY -> 0
                        kotlinx.datetime.DayOfWeek.TUESDAY -> 1
                        kotlinx.datetime.DayOfWeek.WEDNESDAY -> 2
                        kotlinx.datetime.DayOfWeek.THURSDAY -> 3
                        kotlinx.datetime.DayOfWeek.FRIDAY -> 4
                        kotlinx.datetime.DayOfWeek.SATURDAY -> 5
                        else -> 6
                    }
                    val weekStart = today.plus(-offsetFromMonday, kotlinx.datetime.DateTimeUnit.DAY)
                    val weekEnd = weekStart.plus(6, kotlinx.datetime.DateTimeUnit.DAY)
                    evtDate >= weekStart && evtDate <= weekEnd
                }
                DateRangeFilter.THIS_MONTH -> evtDate.year == today.year && evtDate.month == today.month
            }
            val matchesGuests = when (selectedGuestCount) {
                GuestCountFilter.ANY -> true
                GuestCountFilter.SMALL -> event.guestCount < 25
                GuestCountFilter.MEDIUM -> event.guestCount in 25..49
                GuestCountFilter.LARGE -> event.guestCount in 50..99
                GuestCountFilter.XLARGE -> event.guestCount >= 100
            }
            matchesSearch && matchesStatus && matchesDate && matchesGuests
        }

        Row(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {

            AnimatedVisibility(
                visible = sidebarExpanded,
                enter = expandHorizontally(),
                exit = shrinkHorizontally(),
            ) {
                DashboardSidebar(
                    logoUrl = selectedLocation?.logoUrl,
                    locationName = selectedLocation?.name ?: "Max's",
                    onEventsClick = { navigator.push(EventListScreen(initialLocationId = state.selectedLocationId)) },
                    onAddEventClick = {
                        addEventPrefilledDate = (if (state.viewMode == CalendarViewMode.MONTH) state.selectedDate ?: currentLocalDate() else state.currentMonth).atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                        showAddEventDialog = true
                    }
                )
            }

            Column(modifier = Modifier.weight(1f).fillMaxHeight()) {

                DashboardTopBar(
                    searchQuery = state.searchQuery,
                    onSearchChange = screenModel::setSearchQuery,
                    currentUser = state.currentUser,
                    onToggleSidebar = { sidebarExpanded = !sidebarExpanded },
                    isOwner = state.currentUser?.role == UserRole.OWNER,
                    showSwitchLocation = state.locations.size > 1 || state.currentUser?.role == UserRole.OWNER,
                    onSettingsClick = { navigator.push(SettingsScreen()) },
                    onAdminClick = { navigator.push(AdminDashboardScreen()) },
                    onSwitchLocationClick = { navigator.push(com.example.event_management_system.ui.screens.location.LocationPickerScreen()) },
                    onSignOutClick = screenModel::signOut,
                    showFilters = showFilters,
                    onToggleFilters = { showFilters = !showFilters },
                    activeFilterCount = activeFilterCount
                )

                if (showFilters) {
                    FilterPanel(
                        selectedStatuses = selectedStatuses,
                        onStatusToggle = { status ->
                            selectedStatuses = if (status in selectedStatuses)
                                selectedStatuses - status else selectedStatuses + status
                        },
                        selectedDateRange = selectedDateRange,
                        onDateRangeSelect = { selectedDateRange = it },
                        selectedGuestCount = selectedGuestCount,
                        onGuestCountSelect = { selectedGuestCount = it },
                        onClearAll = {
                            selectedStatuses = emptySet()
                            selectedDateRange = DateRangeFilter.ALL
                            selectedGuestCount = GuestCountFilter.ANY
                        },
                        hasActiveFilters = filtersActive
                    )
                }

                Column(modifier = Modifier.weight(1f).fillMaxWidth()) {
                    if (state.searchQuery.isNotBlank() || filtersActive) {
                        if (filteredEvents.isEmpty()) {
                            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                Text(
                                    if (state.searchQuery.isNotBlank()) "No results for \"${state.searchQuery}\""
                                    else "No events match the selected filters",
                                    style = MaterialTheme.typography.bodyLarge,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        } else {
                            LazyColumn(
                                modifier = Modifier.fillMaxSize().padding(horizontal = 20.dp, vertical = 8.dp),
                                verticalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                items(filteredEvents) { event ->
                                    EventListItem(
                                        event = event,
                                        onClick = { navigator.push(EventDetailScreen(event.id)) }
                                    )
                                }
                            }
                        }
                    } else {

                    AnimatedVisibility(
                        visible = upcomingEvents.isNotEmpty(),
                        enter = expandVertically() + fadeIn(),
                        exit = shrinkVertically() + fadeOut(),
                    ) {
                        UpcomingEventsBanner(
                            events = upcomingEvents,
                            onEventClick = { event -> navigator.push(EventDetailScreen(event.id)) }
                        )
                    }

                    Column(
                        modifier = Modifier
                            .weight(1f)
                            .fillMaxWidth()
                            .padding(start = 20.dp, end = 20.dp, top = 4.dp, bottom = 8.dp)
                    ) {
                        Surface(
                            modifier = Modifier.fillMaxWidth().weight(1f),
                            shape = RoundedCornerShape(16.dp),
                            color = MaterialTheme.colorScheme.surface,
                            shadowElevation = 2.dp
                        ) {
                            Column(modifier = Modifier.padding(12.dp).fillMaxSize()) {
                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Row(
                                        verticalAlignment = Alignment.CenterVertically,
                                        horizontalArrangement = Arrangement.spacedBy(2.dp)
                                    ) {
                                        IconButton(
                                            onClick = screenModel::previousPeriod,
                                            modifier = Modifier.size(32.dp)
                                        ) {
                                            Icon(
                                                Icons.Default.KeyboardArrowLeft,
                                                contentDescription = "Previous",
                                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                        val headerText = formatPeriodHeader(
                                            state.currentMonth,
                                            state.viewMode
                                        )
                                        Text(
                                            text = headerText,
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.SemiBold,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                        IconButton(
                                            onClick = screenModel::nextPeriod,
                                            modifier = Modifier.size(32.dp)
                                        ) {
                                            Icon(
                                                Icons.Default.KeyboardArrowRight,
                                                contentDescription = "Next",
                                                tint = MaterialTheme.colorScheme.onSurfaceVariant
                                            )
                                        }
                                    }

                                    ViewModeToggle(
                                        selected = state.viewMode,
                                        onSelect = screenModel::setViewMode
                                    )

                                    Button(
                                        onClick = {
                                            addEventPrefilledDate = (if (state.viewMode == CalendarViewMode.MONTH) state.selectedDate ?: currentLocalDate() else state.currentMonth).atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                            showAddEventDialog = true
                                        },
                                        shape = RoundedCornerShape(50),
                                        colors = ButtonDefaults.buttonColors(
                                            containerColor = MaterialTheme.colorScheme.primary
                                        ),
                                        contentPadding = PaddingValues(
                                            horizontal = 16.dp,
                                            vertical = 8.dp
                                        )
                                    ) {
                                        Icon(
                                            Icons.Default.Add,
                                            contentDescription = null,
                                            modifier = Modifier.size(18.dp)
                                        )
                                        Spacer(Modifier.width(6.dp))
                                        Text(
                                            "Add Event",
                                            style = MaterialTheme.typography.labelLarge
                                        )
                                    }
                                }

                                Spacer(Modifier.height(8.dp))

                                Box(modifier = Modifier.weight(1f).fillMaxWidth()) {
                                    when {
                                        state.isLoading -> LoadingIndicator()
                                        state.error != null -> Text(
                                            state.error!!,
                                            color = MaterialTheme.colorScheme.error
                                        )
                                        else -> when (state.viewMode) {
                                            CalendarViewMode.DAY -> CalendarDayView(
                                                date = state.currentMonth,
                                                events = filteredEvents,
                                                onEventClick = { event ->
                                                    navigator.push(EventDetailScreen(event.id))
                                                },
                                                modifier = Modifier.fillMaxSize()
                                            )
                                            CalendarViewMode.WEEK -> CalendarWeekView(
                                                anchorDate = state.currentMonth,
                                                events = filteredEvents,
                                                onEventClick = { event ->
                                                    navigator.push(EventDetailScreen(event.id))
                                                },
                                                onDayClick = { date ->
                                                    addEventPrefilledDate = date.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                                    showAddEventDialog = true
                                                },
                                                modifier = Modifier.fillMaxSize()
                                            )
                                            CalendarViewMode.MONTH -> CalendarGrid(
                                                currentMonth = state.currentMonth,
                                                events = filteredEvents,
                                                onEventClick = { event ->
                                                    navigator.push(EventDetailScreen(event.id))
                                                },
                                                onDayClick = { date ->
                                                    addEventPrefilledDate = date.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                                                    showAddEventDialog = true
                                                },
                                                modifier = Modifier.fillMaxSize()
                                            )
                                            CalendarViewMode.YEAR -> CalendarYearView(
                                                year = state.currentMonth.year,
                                                currentMonth = state.currentMonth,
                                                onMonthClick = { monthStart ->
                                                    screenModel.navigateToMonth(monthStart)
                                                },
                                                modifier = Modifier.fillMaxSize()
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    } // end else (no search)
                }
            }
        }

        if (showAddEventDialog) {
            EventFormDialog(
                prefilledDate = addEventPrefilledDate,
                initialLocationId = state.selectedLocationId,
                onDismiss = { showAddEventDialog = false },
                onSaved = {
                    showAddEventDialog = false
                    screenModel.refresh()
                }
            )
        }
    }
}

@Composable
private fun DashboardSidebar(
    logoUrl: String?,
    locationName: String,
    onEventsClick: () -> Unit,
    onAddEventClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .width(220.dp)
            .fillMaxHeight()
            .background(MaterialTheme.colorScheme.surface)
            .padding(vertical = 20.dp)
    ) {
        if (logoUrl != null) {
            AsyncImage(
                model = logoUrl,
                contentDescription = locationName,
                contentScale = ContentScale.Fit,
                modifier = Modifier
                    .fillMaxWidth()
                    .height(60.dp)
                    .padding(horizontal = 16.dp)
            )
        } else {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(60.dp)
                    .padding(horizontal = 16.dp),
                contentAlignment = Alignment.CenterStart
            ) {
                Text(
                    text = locationName,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }

        Spacer(Modifier.height(24.dp))

        SidebarSectionLabel("Menu")
        SidebarNavItem(Icons.Outlined.EventNote, "Events", onClick = onEventsClick)
        SidebarNavItem(Icons.Outlined.FolderOpen, "Add Event", onClick = onAddEventClick)
        SidebarNavItem(
            Icons.Outlined.CalendarToday,
            "Calendar",
            isActive = true,
            onClick = {}
        )

        Spacer(Modifier.weight(1f))
    }
}

@Composable
private fun ViewModeToggle(
    selected: CalendarViewMode,
    onSelect: (CalendarViewMode) -> Unit
) {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(50))
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.4f))
            .padding(4.dp),
        horizontalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        CalendarViewMode.entries.forEach { mode ->
            val isSelected = mode == selected
            val bg = if (isSelected) MaterialTheme.colorScheme.surface else Color.Transparent
            val fg = if (isSelected) MaterialTheme.colorScheme.onSurface
                else MaterialTheme.colorScheme.onSurfaceVariant
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(50))
                    .background(bg)
                    .clickable { onSelect(mode) }
                    .padding(horizontal = 14.dp, vertical = 6.dp),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = mode.name.lowercase().replaceFirstChar { it.uppercaseChar() },
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = if (isSelected) FontWeight.SemiBold else FontWeight.Normal,
                    color = fg
                )
            }
        }
    }
}

@Composable
private fun SidebarSectionLabel(text: String) {
    Text(
        text = text,
        modifier = Modifier.padding(horizontal = 16.dp, vertical = 6.dp),
        style = MaterialTheme.typography.labelSmall,
        fontWeight = FontWeight.SemiBold,
        color = MaterialTheme.colorScheme.onSurfaceVariant
    )
}

@Composable
private fun SidebarNavItem(
    icon: ImageVector,
    label: String,
    onClick: () -> Unit,
    isActive: Boolean = false
) {
    val bgColor = if (isActive) MaterialTheme.colorScheme.primary else Color.Transparent
    val contentColor = if (isActive) Color.White else MaterialTheme.colorScheme.onSurfaceVariant

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 10.dp, vertical = 2.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(bgColor)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            imageVector = icon,
            contentDescription = label,
            tint = contentColor,
            modifier = Modifier.size(18.dp)
        )
        Spacer(Modifier.width(10.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = contentColor,
            modifier = Modifier.weight(1f),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun DashboardTopBar(
    searchQuery: String,
    onSearchChange: (String) -> Unit,
    currentUser: User?,
    onToggleSidebar: () -> Unit,
    isOwner: Boolean,
    showSwitchLocation: Boolean,
    onSettingsClick: () -> Unit,
    onAdminClick: () -> Unit,
    onSwitchLocationClick: () -> Unit,
    onSignOutClick: () -> Unit,
    showFilters: Boolean = false,
    onToggleFilters: () -> Unit = {},
    activeFilterCount: Int = 0
) {
    var showUserMenu by remember { mutableStateOf(false) }
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(MaterialTheme.colorScheme.surface)
                .padding(horizontal = 20.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(onClick = onToggleSidebar) {
                Icon(
                    Icons.Default.Menu,
                    contentDescription = "Toggle menu",
                    tint = MaterialTheme.colorScheme.onSurface
                )
            }
            Spacer(Modifier.width(8.dp))

            OutlinedTextField(
                value = searchQuery,
                onValueChange = onSearchChange,
                modifier = Modifier.weight(1f).height(50.dp),
                placeholder = {
                    Text(
                        "Search here...",
                        style = MaterialTheme.typography.bodyMedium
                    )
                },
                leadingIcon = {
                    Icon(
                        Icons.Outlined.Search,
                        contentDescription = null,
                        modifier = Modifier.size(18.dp),
                        tint = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                },
                shape = RoundedCornerShape(24.dp),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
                    focusedBorderColor = MaterialTheme.colorScheme.primary
                )
            )

            Spacer(Modifier.width(4.dp))

            Box {
                IconButton(onClick = onToggleFilters) {
                    Icon(
                        Icons.Outlined.FilterList,
                        contentDescription = "Filters",
                        tint = if (activeFilterCount > 0) MaterialTheme.colorScheme.primary
                               else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
                if (activeFilterCount > 0) {
                    Box(
                        modifier = Modifier
                            .size(9.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary)
                            .align(Alignment.TopEnd)
                            .offset(x = (-6).dp, y = 6.dp)
                    )
                }
            }

            Spacer(Modifier.width(8.dp))

            Box {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .clickable { showUserMenu = true }
                        .padding(horizontal = 8.dp, vertical = 4.dp)
                ) {
                    Box(
                        modifier = Modifier
                            .size(36.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.colorScheme.primary),
                        contentAlignment = Alignment.Center
                    ) {
                        val initials = currentUser?.displayName
                            ?.split(" ")
                            ?.mapNotNull { it.firstOrNull()?.uppercaseChar() }
                            ?.take(2)
                            ?.joinToString("") ?: "?"
                        Text(
                            initials,
                            color = Color.White,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp
                        )
                    }
                    Column {
                        Text(
                            text = currentUser?.displayName ?: "User",
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.SemiBold,
                            color = MaterialTheme.colorScheme.onSurface
                        )
                        Text(
                            text = currentUser?.email ?: "",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    Icon(
                        Icons.Outlined.ExpandMore,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.size(18.dp)
                    )
                }
                DropdownMenu(
                    expanded = showUserMenu,
                    onDismissRequest = { showUserMenu = false }
                ) {
                    DropdownMenuItem(
                        leadingIcon = { Icon(Icons.Outlined.Settings, contentDescription = null) },
                        text = { Text("Settings") },
                        onClick = { showUserMenu = false; onSettingsClick() }
                    )
                    if (isOwner) {
                        DropdownMenuItem(
                            leadingIcon = { Icon(Icons.Outlined.Shield, contentDescription = null) },
                            text = { Text("Admin") },
                            onClick = { showUserMenu = false; onAdminClick() }
                        )
                    }
                    if (showSwitchLocation) {
                        DropdownMenuItem(
                            leadingIcon = { Icon(Icons.Filled.SwapHoriz, contentDescription = null) },
                            text = { Text("Switch Location") },
                            onClick = { showUserMenu = false; onSwitchLocationClick() }
                        )
                    }
                    DropdownMenuItem(
                        leadingIcon = { Icon(Icons.Outlined.HelpOutline, contentDescription = null) },
                        text = { Text("Help & Support") },
                        onClick = { showUserMenu = false }
                    )
                    HorizontalDivider()
                    DropdownMenuItem(
                        leadingIcon = {
                            Icon(
                                Icons.AutoMirrored.Outlined.ExitToApp,
                                contentDescription = null,
                                tint = MaterialTheme.colorScheme.error
                            )
                        },
                        text = { Text("Sign Out", color = MaterialTheme.colorScheme.error) },
                        onClick = { showUserMenu = false; onSignOutClick() }
                    )
                }
            }
        }
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
    }
}

@Composable
private fun UpcomingEventsBanner(
    events: List<Event>,
    onEventClick: (Event) -> Unit
) {
    val scrollState = rememberScrollState()
    val scope = rememberCoroutineScope()

    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 20.dp, vertical = 6.dp),
        shape = RoundedCornerShape(6.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        shadowElevation = 1.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 20.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.width(160.dp)) {
                Text(
                    "Upcoming Events",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.onSurface
                )
                Text(
                    text = if (events.isEmpty()) "none scheduled"
                           else "${events.size} on the way",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Spacer(Modifier.width(8.dp))
            IconButton(
                onClick = { scope.launch { scrollState.animateScrollTo(maxOf(0, scrollState.value - 320)) } },
                modifier = Modifier.size(28.dp),
                enabled = scrollState.value > 0
            ) {
                Icon(
                    Icons.Default.KeyboardArrowLeft,
                    contentDescription = "Scroll left",
                    tint = if (scrollState.value > 0) MaterialTheme.colorScheme.onSurfaceVariant
                           else MaterialTheme.colorScheme.outlineVariant
                )
            }
            Row(
                modifier = Modifier
                    .weight(1f)
                    .horizontalScroll(scrollState),
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                events.forEach { event ->
                    UpcomingEventCard(event = event, onClick = { onEventClick(event) })
                }
            }
            IconButton(
                onClick = { scope.launch { scrollState.animateScrollTo(minOf(scrollState.maxValue, scrollState.value + 320)) } },
                modifier = Modifier.size(28.dp),
                enabled = scrollState.value < scrollState.maxValue
            ) {
                Icon(
                    Icons.Default.KeyboardArrowRight,
                    contentDescription = "Scroll right",
                    tint = if (scrollState.value < scrollState.maxValue) MaterialTheme.colorScheme.onSurfaceVariant
                           else MaterialTheme.colorScheme.outlineVariant
                )
            }
        }
    }
}

@Composable
private fun UpcomingEventCard(event: Event, onClick: () -> Unit) {
    val (accent, tint) = when (event.status) {
        EventStatus.PENDING   -> EventPendingAccent   to EventPending
        EventStatus.CONFIRMED -> EventConfirmedAccent to EventConfirmed
        EventStatus.COMPLETED -> EventCompletedAccent to EventCompleted
        EventStatus.CANCELLED -> EventCancelledAccent to EventCancelled
    }
    val eventDate = Instant.fromEpochMilliseconds(event.eventDate)
        .toLocalDateTime(TimeZone.UTC).date
    val monthShort = eventDate.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }

    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(10.dp))
            .background(tint)
            .clickable(onClick = onClick)
            .padding(6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(
            modifier = Modifier
                .size(44.dp)
                .clip(RoundedCornerShape(6.dp))
                .background(accent),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Text(
                text = monthShort,
                color = Color.White,
                fontSize = 10.sp,
                lineHeight = 11.sp,
                fontWeight = FontWeight.Medium
            )
            Text(
                text = eventDate.dayOfMonth.toString(),
                fontWeight = FontWeight.Bold,
                color = Color.White,
                fontSize = 16.sp,
                lineHeight = 18.sp
            )
        }
        Spacer(Modifier.width(10.dp))
        Column(modifier = Modifier.padding(end = 10.dp).widthIn(max = 140.dp)) {
            Text(
                text = event.customerName,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = buildString {
                    if (event.eventTime.isNotBlank()) append(event.eventTime)
                    if (event.eventTime.isNotBlank()) append(" · ")
                    append("${event.guestCount} guests")
                },
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun FilterPanel(
    selectedStatuses: Set<EventStatus>,
    onStatusToggle: (EventStatus) -> Unit,
    selectedDateRange: DateRangeFilter,
    onDateRangeSelect: (DateRangeFilter) -> Unit,
    selectedGuestCount: GuestCountFilter,
    onGuestCountSelect: (GuestCountFilter) -> Unit,
    onClearAll: () -> Unit,
    hasActiveFilters: Boolean
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = MaterialTheme.colorScheme.surface,
        shadowElevation = 0.dp
    ) {
        Column {
            Column(modifier = Modifier.padding(horizontal = 20.dp, vertical = 10.dp)) {
                FilterRow(label = "Status") {
                    EventStatus.entries.forEach { status ->
                        FilterChipItem(
                            label = status.name.lowercase().replaceFirstChar { it.uppercase() },
                            selected = status in selectedStatuses,
                            onClick = { onStatusToggle(status) }
                        )
                    }
                }
                Spacer(Modifier.height(6.dp))
                FilterRow(label = "Date") {
                    DateRangeFilter.entries.forEach { range ->
                        FilterChipItem(
                            label = range.label,
                            selected = range == selectedDateRange,
                            onClick = { onDateRangeSelect(range) }
                        )
                    }
                }
                Spacer(Modifier.height(6.dp))
                FilterRow(label = "Guests") {
                    GuestCountFilter.entries.forEach { count ->
                        FilterChipItem(
                            label = count.label,
                            selected = count == selectedGuestCount,
                            onClick = { onGuestCountSelect(count) }
                        )
                    }
                }
                if (hasActiveFilters) {
                    Spacer(Modifier.height(4.dp))
                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                        TextButton(onClick = onClearAll) {
                            Text("Clear filters", style = MaterialTheme.typography.labelMedium)
                        }
                    }
                }
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        }
    }
}

@Composable
private fun FilterRow(label: String, content: @Composable () -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            fontWeight = FontWeight.SemiBold,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.width(48.dp)
        )
        Row(
            modifier = Modifier.weight(1f).horizontalScroll(rememberScrollState()),
            horizontalArrangement = Arrangement.spacedBy(6.dp)
        ) {
            content()
        }
    }
}

@Composable
private fun FilterChipItem(label: String, selected: Boolean, onClick: () -> Unit) {
    val bg = if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceVariant
    val fg = if (selected) Color.White else MaterialTheme.colorScheme.onSurfaceVariant
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(50))
            .background(bg)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 5.dp),
        contentAlignment = Alignment.Center
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
            color = fg
        )
    }
}

private fun parseEventTime(raw: String): Pair<Int, Int>? {
    val trimmed = raw.trim()
    if (trimmed.isEmpty()) return null
    val regex = Regex("^\\s*(\\d{1,2})(?::(\\d{2}))?\\s*(am|pm)?\\s*$", RegexOption.IGNORE_CASE)
    val match = regex.matchEntire(trimmed) ?: return null
    var hour = match.groupValues[1].toIntOrNull() ?: return null
    val minute = match.groupValues[2].toIntOrNull() ?: 0
    val period = match.groupValues[3].lowercase()
    if (period == "pm" && hour < 12) hour += 12
    if (period == "am" && hour == 12) hour = 0
    if (hour !in 0..23 || minute !in 0..59) return null
    return hour to minute
}

private fun formatPeriodHeader(date: kotlinx.datetime.LocalDate, mode: CalendarViewMode): String {
    val monthShort = date.month.name.take(3).lowercase().replaceFirstChar { it.uppercaseChar() }
    val monthFull = date.month.name.lowercase().replaceFirstChar { it.uppercaseChar() }
    return when (mode) {
        CalendarViewMode.DAY -> "$monthShort ${date.dayOfMonth}, ${date.year}"
        CalendarViewMode.WEEK -> {
            val offset = when (date.dayOfWeek) {
                kotlinx.datetime.DayOfWeek.SUNDAY -> 0
                kotlinx.datetime.DayOfWeek.MONDAY -> 1
                kotlinx.datetime.DayOfWeek.TUESDAY -> 2
                kotlinx.datetime.DayOfWeek.WEDNESDAY -> 3
                kotlinx.datetime.DayOfWeek.THURSDAY -> 4
                kotlinx.datetime.DayOfWeek.FRIDAY -> 5
                kotlinx.datetime.DayOfWeek.SATURDAY -> 6
                else -> 0
            }
            val sunday = date.plus(-offset, kotlinx.datetime.DateTimeUnit.DAY)
            val saturday = sunday.plus(6, kotlinx.datetime.DateTimeUnit.DAY)
            val sMon = sunday.month.name.take(3).lowercase().replaceFirstChar { it.uppercaseChar() }
            val eMon = saturday.month.name.take(3).lowercase().replaceFirstChar { it.uppercaseChar() }
            if (sunday.month == saturday.month) {
                "$sMon ${sunday.dayOfMonth} - ${saturday.dayOfMonth}, ${sunday.year}"
            } else {
                "$sMon ${sunday.dayOfMonth} - $eMon ${saturday.dayOfMonth}, ${saturday.year}"
            }
        }
        CalendarViewMode.MONTH -> "$monthFull ${date.year}"
        CalendarViewMode.YEAR -> "${date.year}"
    }
}
